/*
 * What a technician calls a timer and a zone, in the operator's words:
 * "Bungalows", "Bungalows 4", "Zone 4". Shared because the survey and the
 * proposal must never disagree about a name: a client reading "Bungalows 4" on
 * a bid has to find the same zone on the screen the technician walked it with.
 *
 * Structural, so the survey's DTOs and the proposal builder's rows both fit.
 * Every caller passes the site's timers in the order they were recorded, which
 * is what numbers timers nobody has named.
 */

/** What a timer is named by. */
export interface NamedTimer {
  id: string
  label: string | null
  /** The area its pin sits in, when it sits in one. */
  areaName: string | null
}

/** What a zone is named by. */
export interface NumberedZone {
  number: number
  timerId: string
}

/**
 * How one timer reads: `name` alone, and `zonePrefix` before a zone's number.
 * `generic` is set when every word came from the app rather than from
 * somebody typing it, so it may lose its capital inside a sentence.
 */
interface TimerWords {
  name: string
  /** "Bungalows" for "Bungalows 4"; null when a zone reads "…, zone 4". */
  zonePrefix: string | null
  generic: boolean
}

/** A letter or a number on its own ("A", "2") reads "Timer A"; a word as typed. */
function shownLabel(label: string): string {
  return /^[A-Za-z0-9]{1,2}$/.test(label) ? `Timer ${label}` : label
}

function sameWords(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * A named timer is called what the crew typed. An unnamed one is called after
 * its area ("Bungalows timer"), unless that would read the same as another
 * timer in it: a park can have two controllers, and two "Bungalows 4"s on a
 * bid are two different zones nobody can tell apart. Those are numbered
 * within their area ("Bungalows timer 2"). Unnamed timers outside any area are
 * numbered across the site, or plain "Timer" when it has only one.
 */
function wordsFor(
  timer: NamedTimer,
  timers: readonly NamedTimer[],
): TimerWords {
  if (timer.label !== null) {
    return {
      name: shownLabel(timer.label),
      zonePrefix: timer.label,
      generic: false,
    }
  }
  const area = timer.areaName
  if (area !== null) {
    const unnamedHere = timers.filter(
      (item) => item.label === null && item.areaName === area,
    )
    const areaIsTaken = timers.some(
      (item) => item.label !== null && sameWords(item.label, area),
    )
    if (unnamedHere.length <= 1 && !areaIsTaken) {
      return { name: `${area} timer`, zonePrefix: area, generic: false }
    }
    const position = unnamedHere.findIndex((item) => item.id === timer.id)
    return {
      name: `${area} timer ${String(position + 1)}`,
      zonePrefix: null,
      generic: false,
    }
  }
  if (timers.length <= 1)
    return { name: 'Timer', zonePrefix: null, generic: true }
  const position = timers
    .filter((item) => item.label === null && item.areaName === null)
    .findIndex((item) => item.id === timer.id)
  return {
    name: `Timer ${String(position + 1)}`,
    zonePrefix: null,
    generic: true,
  }
}

/** What tells a timer apart: "Bungalows", "Timer A", "Founders timer 2". */
export function timerName(
  timer: NamedTimer,
  timers: readonly NamedTimer[],
): string {
  return wordsFor(timer, timers).name
}

/**
 * One or more zones of one timer, as words. `numbers` is already written out
 * ("4", "2–5"); `plural` says whether it is more than one zone.
 */
function zonesWords(
  timerId: string,
  numbers: string,
  plural: boolean,
  timers: readonly NamedTimer[],
): { name: string; generic: boolean } {
  const zone = plural ? 'zones' : 'zone'
  const timer = timers.find((item) => item.id === timerId)
  if (timers.length <= 1 || timer === undefined) {
    return { name: `${plural ? 'Zones' : 'Zone'} ${numbers}`, generic: true }
  }
  const words = wordsFor(timer, timers)
  return words.zonePrefix === null
    ? { name: `${words.name}, ${zone} ${numbers}`, generic: words.generic }
    : { name: `${words.zonePrefix} ${numbers}`, generic: false }
}

function zoneWords(
  zone: NumberedZone,
  timers: readonly NamedTimer[],
): { name: string; generic: boolean } {
  return zonesWords(zone.timerId, String(zone.number), false, timers)
}

/**
 * A zone, as a technician says it: "Zone 4" on a site with one timer, else
 * its timer's name and the number ("Bungalows 4", "A 4"), or "Bungalows
 * timer 2, zone 4" for a timer that has to be numbered.
 */
export function zoneName(
  zone: NumberedZone,
  timers: readonly NamedTimer[],
): string {
  return zoneWords(zone, timers).name
}

/**
 * A zone's name inside a sentence: "Valve, zone 4", but "Valve, Bungalows
 * 4". Only the app's own words lose their capital; a name somebody typed
 * keeps it.
 */
export function zoneNameInSentence(
  zone: NumberedZone,
  timers: readonly NamedTimer[],
): string {
  const { name, generic } = zoneWords(zone, timers)
  return generic ? name.charAt(0).toLowerCase() + name.slice(1) : name
}

/**
 * Zone numbers as somebody would write them: "2–5", "17 and 23", "1–3 and
 * 7". Three or more in a row are a range; two in a row are just listed.
 */
export function zoneNumbersText(numbers: readonly number[]): string {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b)
  const parts: string[] = []
  let start = 0
  for (let index = 1; index <= sorted.length; index += 1) {
    const current = sorted[index]
    const previous = sorted[index - 1]
    if (
      current !== undefined &&
      previous !== undefined &&
      current === previous + 1
    ) {
      continue
    }
    const run = sorted.slice(start, index)
    const first = run[0]
    const last = run.at(-1)
    if (run.length >= 3 && first !== undefined && last !== undefined) {
      parts.push(`${String(first)}–${String(last)}`)
    } else {
      parts.push(...run.map(String))
    }
    start = index
  }
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts.at(-1) ?? ''}`
}

/**
 * Several zones of one timer, named together: "Zones 2–5" on a site with
 * one timer, "Bungalows 2–5", or "Bungalows timer 2, zones 2 and 4". One
 * zone is named as `zoneName` names it.
 */
export function zonesName(
  timerId: string,
  numbers: readonly number[],
  timers: readonly NamedTimer[],
): string {
  return zonesWords(
    timerId,
    zoneNumbersText(numbers),
    new Set(numbers).size > 1,
    timers,
  ).name
}
